import { useMemo, useState } from 'react';
import { Icon } from '@nazka/nazka.mapframe.components';
import {
  faClose,
  faEmptySet,
  faSave,
  faWarning,
} from '@fortawesome/pro-light-svg-icons';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';

import AdminActions from '@App/pages/Admin/AdminActions/AdminActions';
import AdminLastUpdate from '@App/pages/Admin/AdminLastUpdate/AdminLastUpdate';
import queryClient from '@App/app.query-client';

import { categoryIcons, categoryLabels } from '@App/pages/MobilityPlan/mobility-plan.constants';
import { useCompanyMobilityOffer } from '@services/query.service';
import { updateCompanyMobilityOffer } from '@services/company.service';
import { updateCompanyMobilityOffer as updateCompanyMobilityOfferRedux } from '../Map/map.slice';

import TransferList from './TransferList/TransferList';

import { createUseableMobilityOffer } from './edit-mobility.utils';

import './edit-mobility-offer.styl';

function EditMobilityOffer({
  companyId,
  onCancel,
  isAdmin = false,
  OIDN,
}) {
  const [updatedMobilityOffer, setUpdatedMobilityOffer] = useState();

  const { data } = useCompanyMobilityOffer(companyId);
  const dispatch = useDispatch();

  if (!companyId) {
    onCancel();
  }

  const useableMobilityOffer = useMemo(() => {
    const { mobilityOffer, mobilityFields } = data || {};

    return createUseableMobilityOffer(updatedMobilityOffer ?? mobilityOffer, mobilityFields);
  }, [data, updatedMobilityOffer]);

  const updateMutation = useMutation({
    mutationFn: () => updateCompanyMobilityOffer(companyId, updatedMobilityOffer),
    onSuccess: async (result) => {
      if (isAdmin) {
        await queryClient.invalidateQueries({ queryKey: ['user', 'companies'] });
      } else if (OIDN) {
        dispatch(updateCompanyMobilityOfferRedux({ OIDN, mobilityOffer: result }));
        await queryClient.invalidateQueries({ queryKey: ['vacancy'] });
      }

      toast.success('Jouw aanpassingen zijn opgeslagen');
      onCancel();
    },
    onError: () => {
      toast.error('Er ging helaas iets mis bij het opslaan');
    },
  });

  return (
    <>
      {isAdmin && (
        <div className="edit-mobility-offer__alert">
          <Icon
            icon={faWarning}
            className="edit-mobility-offer__alert__icon"
          />
          <p className="edit-mobility-offer__alert__text">
            Hier kan je de mobiliteitsvoordelen voor je werknemers invullen.
            Let erop dat dit gaat over voordelen voor alle werknemers, zoals
            kleedkamers en het aantal fietsparkeerplaatsen. Functiespecifieke
            voordelen horen bij een vacature.
          </p>
        </div>
      )}
      <AdminActions
        actions={[
          {
            id: 'leegmaken',
            text: 'Leegmaken',
            onClick: () => setUpdatedMobilityOffer({}),
            icon: faEmptySet,
          },
          {
            id: 'opslaan',
            text: 'Opslaan',
            disabled: !updatedMobilityOffer,
            icon: faSave,
            isLoading: updateMutation.isPending,
            onClick: () => updateMutation.mutate(),
          },
          {
            id: 'annuleren',
            text: 'Annuleren',
            onClick: onCancel,
            icon: faClose,
          },
        ]}
      />
      {data?.mobilityOffer && (
        <AdminLastUpdate
          updatedAt={data.mobilityOffer.updatedAt}
          updatedBy={data.mobilityOffer.updatedBy}
          verified={data.mobilityOffer.verified}
          isAdmin={isAdmin}
        />
      )}
      <div className="edit-mobility-offer__form">
        {useableMobilityOffer?.map(({ category, fields }) => {
          return (
            <div className="edit-mobility-offer__form__category" key={category}>
              <h3 className="edit-mobility-offer__form__title">
                <Icon
                  icon={categoryIcons[category]}
                  size="24px"
                />
                {categoryLabels[category]}
              </h3>
              <TransferList
                fields={fields}
                mobilityOffer={updatedMobilityOffer ?? data?.mobilityOffer}
                setMobilityOffer={setUpdatedMobilityOffer}
                isAdmin={isAdmin}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}

export default EditMobilityOffer;